import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewKustannuspaikkaMutation } from '../../api/kustannuspaikatApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function KustannuspaikkaList({ kustannuspaikat, handleDeleteKustannuspaikka, handleUpdateKustannuspaikka, fetchData, onSuccess, onError }) {
    const [selectedKustannuspaikka, setSelectedKustannuspaikka] = useState(null);
    const [newKustannuspaikkaData, setNewKustannuspaikkaData] = useState({ kustannuspaikka: '' });
    const [addKustannuspaikkaData, setAddKustannuspaikkaData] = useState({ kustannuspaikka: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [addNewKustannuspaikka, { isLoading }] = useAddNewKustannuspaikkaMutation();

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredKustannuspaikat = Array.isArray(kustannuspaikat)
        ? kustannuspaikat.filter((Kustannuspaikka) =>
            Kustannuspaikka.kustannuspaikka.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
    const totalPages = Math.ceil(filteredKustannuspaikat.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedKustannuspaikat = filteredKustannuspaikat.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (Kustannuspaikka) => {
        setShowAddForm(false);
        if (selectedKustannuspaikka?.id === Kustannuspaikka.id) {
            setSelectedKustannuspaikka(null);
            setNewKustannuspaikkaData({ kustannuspaikka: '' });
        } else {
            setSelectedKustannuspaikka(Kustannuspaikka);
            setNewKustannuspaikkaData(Kustannuspaikka);
        }
    };

    const handleAddClick = () => {
        setSelectedKustannuspaikka(null);
        setNewKustannuspaikkaData({ kustannuspaikka: '' });
        setShowAddForm(!showAddForm);
    };

    const handleLisääKustannuspaikka = async (Kustannuspaikka) => {
        try {
            const result = await addNewKustannuspaikka(Kustannuspaikka);
            if (result.error) onError('Kustannuspaikan luominen epäonnistui');
            if (!result.error) onSuccess('Kustannuspaikka added successfully.', 'Kustannuspaikka', result.data.id);
            setAddKustannuspaikkaData({ kustannuspaikka: '' });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Kustannuspaikan luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Kustannuspaikat
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Kustannuspaikka List */}
                {displayedKustannuspaikat.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei kustannuspaikkoja näytettäväksi.
                    </Typography>
                ) : (
                    displayedKustannuspaikat.map((Kustannuspaikka) => {
                        const isSelected = selectedKustannuspaikka?.id === Kustannuspaikka.id;

                        return (
                            <Box key={Kustannuspaikka.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(Kustannuspaikka)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {Kustannuspaikka.kustannuspaikka}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            Käytetään hallinnon ja tapahtumien kustannuskohteena
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteKustannuspaikka(event, Kustannuspaikka);
                                            if (selectedKustannuspaikka?.id === Kustannuspaikka.id) {
                                                setSelectedKustannuspaikka(null);
                                                setNewKustannuspaikkaData({ kustannuspaikka: '' });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa kustannuspaikkaa
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="kustannuspaikka"
                                                    label="Kustannuspaikka"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newKustannuspaikkaData.kustannuspaikka}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewKustannuspaikkaData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateKustannuspaikka} data={newKustannuspaikkaData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää kustannuspaikka'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi kustannuspaikka
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addKustannuspaikka"
                                        fieldName="kustannuspaikka"
                                        label="Kustannuspaikka"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addKustannuspaikkaData.kustannuspaikka}
                                        onChange={(event) =>
                                            setAddKustannuspaikkaData({ ...addKustannuspaikkaData, kustannuspaikka: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääKustannuspaikka(addKustannuspaikkaData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredKustannuspaikat.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredKustannuspaikat}
                            array={kustannuspaikat}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default KustannuspaikkaList;